import type { ReactNode } from "react"
import { Tag, Tooltip } from "antd"
import { AlertTriangle, CircleSlash, MinusCircle } from "lucide-react"
import type { TenantSlice, TileResult, TileState } from "@csbi/contracts"
import { formatTimestamp } from "@csbi/viz-core"

const META: Record<Exclude<TileState, "ok">, { icon: typeof CircleSlash; label: string; hint: string; color: string }> = {
  empty: {
    icon: CircleSlash,
    label: "No data",
    hint:
      "The pipeline works — this deployment simply produced no data of this kind in the selected range. " +
      "That is a fact about the data, not a defect.",
    color: "var(--csbi-ink-muted)",
  },
  unsupported: {
    icon: MinusCircle,
    label: "Unsupported",
    hint:
      "This deployment's srv version does not expose this metric yet. It will become available with a " +
      "normal srv release — nothing needs to be scheduled for it separately.",
    color: "var(--csbi-ink-muted)",
  },
  stale: {
    icon: AlertTriangle,
    label: "Collection failing",
    hint: "Collection has failed repeatedly. What you see is the last successfully collected data.",
    color: "var(--csbi-status-warning-ink)",
  },
}

export type StateBadgeProps = {
  state: TileState
  perTenant?: readonly TenantSlice[]
}

export type StateNote = {
  tag: ReactNode
  detail: string
  level: "note" | "warning"
}

export function stateNote({ state, perTenant = [] }: StateBadgeProps): StateNote | null {
  const problems = perTenant.filter((t) => t.state !== "ok")
  if (state === "ok" && problems.length === 0) return null

  const worst: Exclude<TileState, "ok"> =
    state !== "ok"
      ? state
      : problems.some((t) => t.state === "stale")
        ? "stale"
        : problems.some((t) => t.state === "unsupported")
          ? "unsupported"
          : "empty"

  const meta = META[worst]
  const Icon = meta.icon
  const detail = problems.length
    ? problems.map((t) => `${t.displayName}: ${t.stateDetail ?? META[t.state as Exclude<TileState, "ok">].label}`).join("\n")
    : meta.hint

  return {
    level: worst === "stale" ? "warning" : "note",
    detail,
    tag: (
      <Tag
        // A status colour always comes with an icon and text, never colour alone
        icon={<Icon size={12} style={{ verticalAlign: "-2px", marginRight: 4 }} />}
        color={worst === "stale" ? "warning" : "default"}
        style={{ marginInlineEnd: 0 }}
      >
        {problems.length && state === "ok" ? `${meta.label} · ${problems.length}` : meta.label}
      </Tag>
    ),
  }
}

export function StateBadge({ state, perTenant = [] }: StateBadgeProps) {
  const note = stateNote({ state, perTenant })
  if (!note) return null

  return <Tooltip title={<span style={{ whiteSpace: "pre-line" }}>{note.detail}</span>}>{note.tag}</Tooltip>
}

function ZeroBaseline() {
  return (
    <svg className="csbi-zero-baseline" width="100%" height="72" aria-hidden="true" style={{ display: "block", opacity: 0.5 }}>
      <line x1="8%" y1="90%" x2="97%" y2="90%" stroke="var(--csbi-baseline)" strokeWidth="1" />
      <line x1="8%" y1="8%" x2="8%" y2="90%" stroke="var(--csbi-gridline)" strokeWidth="1" />
    </svg>
  )
}

export type TileStatePlaceholderProps = {
  result: Pick<TileResult, "state" | "perTenant" | "meta">
}

export function TileStatePlaceholder({ result }: TileStatePlaceholderProps) {
  const state = result.state === "ok" ? "empty" : result.state
  const meta = META[state]
  const Icon = meta.icon

  const reasons = [...new Set(result.perTenant.filter((t) => t.stateDetail).map((t) => t.stateDetail!))]
  const staleSince = result.perTenant
    .filter((t) => t.state === "stale" && t.lastCollectedAt)
    .map((t) => t.lastCollectedAt!)
    .sort()
    .at(-1)

  return (
    <div
      className="csbi-tile-placeholder"
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 6,
        height: "100%",
        minHeight: 120,
        padding: "12px 16px",
        textAlign: "center",
      }}
    >
      {state === "empty" && <ZeroBaseline />}
      <Icon size={20} color={meta.color} aria-hidden="true" />
      <div style={{ fontSize: 13, fontWeight: 600, color: "var(--csbi-ink-secondary)" }}>
        {meta.label}
        {staleSince ? ` — data as of ${formatTimestamp(staleSince)}` : ""}
      </div>
      <div style={{ fontSize: 12, color: "var(--csbi-ink-muted)", maxWidth: 420, lineHeight: 1.5 }}>
        {reasons.length ? reasons.join(" · ") : meta.hint}
      </div>
    </div>
  )
}
